import { FCX, ID, IDropdownColumn, includesInLC, markMatch } from "@datassential/platform-ui-lib";
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { useCommonDataContext } from "@core/CommonDataContext";
import { containsAll, getIntersection, containsSome } from "@core/utils/array";
import { HeatScore } from "@models/HeatScore";

type TrendFilter = {
	macStages: number[];
	heatScores: HeatScore[];
	categories: number[];
};

export type AppliedCategoryTextData = {
	id: number;
	name: string;
	subCategories: string[];
	isAllSelected: boolean;
}[]

type ItemToFilter = {
    macStage?: number | null;
	heatScore?: HeatScore | null;
    subCategoriesIds?: number[];
};

export type SelectFiltersContextType = {
	expandedCategories: ID[];
	setExpandedCategories: Dispatch<SetStateAction<ID[]>>;
	search: string;
	setSearch: Dispatch<SetStateAction<string>>;
	dropdownGroups: IDropdownColumn[];
	filteredCategories: ID[];
	appliedFilters: TrendFilter;
	setAppliedFilters: Dispatch<SetStateAction<TrendFilter>>;
	selectedFilters: TrendFilter;
	setSelectedFilters: Dispatch<SetStateAction<TrendFilter>>;
	appliedCategoryNamesData: AppliedCategoryTextData;
	appliedMacStageNames: string[];
	getMatchFilters: (item: ItemToFilter) => boolean;
	featuredCategoryId: number | null;
	featuredCategoryName: string | null;
};

export const SelectFiltersContextDefaultData: SelectFiltersContextType = {
	expandedCategories: [],
	setExpandedCategories: () => {
	},
	search: "",
	setSearch: () => {
	},
	dropdownGroups: [],
	filteredCategories: [],
	appliedFilters: {
		macStages: [],
		heatScores: [],
		categories: [],
	},
	setAppliedFilters: () => {
	},
	selectedFilters: {
		macStages: [],
		heatScores: [],
		categories: [],
	},
	setSelectedFilters: () => {
	},
	appliedCategoryNamesData: [],
	appliedMacStageNames: [],
	getMatchFilters: () => false,
	featuredCategoryId: null,
	featuredCategoryName: null,
}

export const SelectFiltersContext = createContext<SelectFiltersContextType>(SelectFiltersContextDefaultData);

export function useGetAppliedCategoryNamesData() {
    const { categories } = useCommonDataContext()
    return useCallback(
        (data: SelectFiltersContextType['appliedFilters']['categories']) => {
            const selectedCategories = data;

            if (!selectedCategories.length || !categories) return []

            const categoriesData: AppliedCategoryTextData  = []
            for (const category of categories) {
                const subCategories = category.subCategories || []
                const subCategoryIds = subCategories.map((subCat) => subCat.id) || []
                const isAnySelected = containsSome(selectedCategories, subCategoryIds)
                if (!isAnySelected) continue

                const isAllSelected = containsAll(selectedCategories, subCategoryIds)

                const matchingSubCategoryIds = getIntersection(subCategoryIds, selectedCategories)
                const subCategoryNames = matchingSubCategoryIds.map(
                    (id) => subCategories.find((subCat) => subCat.id === id)?.name ?? ""
                ).filter(Boolean)

                categoriesData.push({
                    id: category.id ?? 0,
                    name: category.name ?? "",
                    subCategories: subCategoryNames,
                    isAllSelected,
                })
            }

            return categoriesData
        }, [categories]
    );
}

const SelectFiltersProvider: FCX = ({ children }) => {
	const [expandedCategories, setExpandedCategories] = useState<SelectFiltersContextType["expandedCategories"]>(SelectFiltersContextDefaultData.expandedCategories);
	const [search, setSearch] = useState<SelectFiltersContextType["search"]>(SelectFiltersContextDefaultData.search);
	const [filteredCategories, setFilteredCategories] = useState<SelectFiltersContextType["filteredCategories"]>(SelectFiltersContextDefaultData.filteredCategories);
	const [dropdownItems, setDropdownItems] = useState<SelectFiltersContextType["dropdownGroups"]>(SelectFiltersContextDefaultData.dropdownGroups)
	const [appliedFilters, setAppliedFilters] = useState<SelectFiltersContextType['appliedFilters']>(SelectFiltersContextDefaultData.appliedFilters);
	const [selectedFilters, setSelectedFilters] = useState<SelectFiltersContextType['selectedFilters']>(SelectFiltersContextDefaultData.selectedFilters);

	const { categories, macStages } = useCommonDataContext();
    const getAppliedCategoryNamesData = useGetAppliedCategoryNamesData();

	const handleSelectCategory = useCallback((categoryId: number) => () => {
		const filterCategory = (id: number) => id !== categoryId
		setSelectedFilters((prevSelectedFilters) => {
			const newSelectedFilters = structuredClone(prevSelectedFilters);
			if (newSelectedFilters.categories.includes(categoryId)) {
				newSelectedFilters.categories = newSelectedFilters.categories.filter(filterCategory);
			} else {
				newSelectedFilters.categories.push(categoryId);
			}
			return newSelectedFilters;
		});
	}, [setSelectedFilters]);

	useEffect(() => {
		const trimmedSearch = search.trim();
		if (trimmedSearch.length === 0) {
			setDropdownItems([]);
			setFilteredCategories([]);
			return;
		}

		const matchingSubCategories = categories?.flatMap((category) => {
			if (!category?.isEnabled) return [];

			return category.subCategories?.filter((subCategory) => {
				return subCategory?.name && includesInLC(subCategory.name, trimmedSearch);
			});
		}) ?? [];

		const matchedCategoryIds: number[] = []

		const dropdownItems = matchingSubCategories
			.map((subCategory, index) => {
				if (!subCategory) return null;

				const isSelected = selectedFilters.categories.includes(subCategory.id ?? 0);
				const onClick = handleSelectCategory(subCategory.id ?? 0);

				matchedCategoryIds.push(subCategory.id ?? 0);

				return {
					id: subCategory.id,
					items: [],
					content: (
						<div
							key={subCategory.id}
							className={
								cn(
									"SearchForFiltersDropdown__item",
									{
										"is-selected": isSelected,
									}
								)
							}
							data-testid={`buttonFiltersSearch_${index}`}
							onClick={onClick}
							dangerouslySetInnerHTML={{
								__html: `<span>${markMatch(subCategory.name ?? '', trimmedSearch, true)}</span>`,
							}}
						/>
					)
				}
			})
			.filter((item) => item !== null) as IDropdownColumn[];

		setDropdownItems(dropdownItems);
		setFilteredCategories(matchedCategoryIds);
	}, [categories, setFilteredCategories, search, handleSelectCategory, selectedFilters.categories]);

	const dropdownGroups = useMemo(() => {
		const trimmedSearch = search.trim();
		if (trimmedSearch.length === 0) {
			return [];
		}

		return dropdownItems.length > 0 ? dropdownItems : [{
			id: "col-no-results",
			items: [],
			content: (
				<div
					className="SearchForFiltersDropdown__no-results"
					data-testid="textFiltersSearchNoResults"
				>
					No results found!
				</div>
			)
		}];
	}, [search, dropdownItems]);

	const appliedCategoryNamesData = useMemo(
        () => getAppliedCategoryNamesData(appliedFilters.categories),
        [appliedFilters.categories, getAppliedCategoryNamesData]
    );

	const appliedMacStageNames = useMemo(() => {
		return appliedFilters.macStages.map(
			(macStageId) => macStages?.find((macStage) => macStage.id === macStageId)?.name ?? ""
		)
		.filter(Boolean)
	}, [appliedFilters.macStages, macStages])

    const getMatchFilters = useCallback(
        (item: ItemToFilter) => {
            let result = true;

            if (appliedFilters.macStages.length > 0) {
                result = result && appliedFilters.macStages.includes(item.macStage ?? 0);
            }

			if (appliedFilters.heatScores.length > 0) {
				result = result && item.heatScore !== null && appliedFilters.heatScores.includes(item.heatScore ?? 0);
			}

            if (appliedFilters.categories.length > 0) {
				result = result && containsSome(appliedFilters.categories, item.subCategoriesIds ?? []);
            }

            return result;
        },
        [appliedFilters.heatScores, appliedFilters.macStages, appliedFilters.categories]
    );

	const featuredCategoryId = useMemo(() => {
		return appliedCategoryNamesData.length === 1 ? appliedCategoryNamesData[0].id : null
	}, [appliedCategoryNamesData]);

	const featuredCategoryName = useMemo(() => {
		return appliedCategoryNamesData.length === 1 ? appliedCategoryNamesData[0].name : null
	}, [appliedCategoryNamesData]);

	const value = useMemo(() => ({
		expandedCategories,
		setExpandedCategories,
		search,
		setSearch,
		filteredCategories,
		dropdownGroups,
		appliedFilters,
		setAppliedFilters,
		selectedFilters,
		setSelectedFilters,
		appliedCategoryNamesData,
		appliedMacStageNames,
        getMatchFilters,
		featuredCategoryId,
		featuredCategoryName,
	}), [
		expandedCategories,
		setExpandedCategories,
		search,
		setSearch,
		filteredCategories,
		dropdownGroups,
		appliedFilters,
		setAppliedFilters,
		selectedFilters,
		setSelectedFilters,
		appliedCategoryNamesData,
		appliedMacStageNames,
        getMatchFilters,
		featuredCategoryId,
		featuredCategoryName,
	]);

	return (
		<SelectFiltersContext.Provider value={value}>
			{children}
		</SelectFiltersContext.Provider>
	);
};

export const useSelectFilters = () => useContext(SelectFiltersContext);

export default SelectFiltersProvider;